import { isType, Action } from "typescript-fsa";
import update from "immutability-helper";
import log from "loglevel";
import moment from "moment";
import { map, reduce, concat } from "lodash";
import { Moment } from "moment";
import { Map } from "immutable";

import { ApiAction, isFailedResponseUnauthenticatedNotLogin } from "@api/actions";
import { ApiRequest } from "@api/requests";
import { logout } from "@auth/actions";
import { StateError, TrendsPaginationResult } from "@orm/models";
import { equalDateAndMoment } from "@util/index";
import * as actions from "../actions";
import { PlaylistTrendsResult } from "../actions";
import { getDefaultFromDate, getLatestDate } from "../constants";
import {
    SalesKind,
    TrendsResponse,
    TrendsType,
    SalesResponse,
    DemographicsResponse,
    HeadlineStatsResponse,
    SalesInternalState as State,
    TrendsSet,
    SalesSet,
    AlbumSalesSet,
    TrackSalesSet,
    ArtistSalesSet,
    CountrySalesSet,
    DemographicsSet,
    AlbumDemographicsSet,
    TrackDemographicsSet,
    ArtistDemographicsSet,
    HeadlineStatsSet,
    AlbumHeadlineStatsSet,
    TrackHeadlineStatsSet,
    ArtistHeadlineStatsSet,
    PlaylistTrendsSet,
    AlbumPlaylistTrendsSet,
    TrackPlaylistTrendsSet,
    ArtistPlaylistTrendsSet,
} from "../models";

const getInitialState = () => ({
    data: {
        "sales": {
            all: null,
            albums: Map<number, AlbumSalesSet>(),
            tracks: Map<number, TrackSalesSet>(),
            artists: Map<number, ArtistSalesSet>(),
        },
        "demographics": {
            all: null,
            albums: Map<number, DemographicsSet>(),
            tracks: Map<number, DemographicsSet>(),
            artists: Map<number, DemographicsSet>(),
        },
        "playlist-trends": {
            all: null,
            albums: Map<number, PlaylistTrendsSet>(),
            tracks: Map<number, PlaylistTrendsSet>(),
            artists: Map<number, PlaylistTrendsSet>(),
        },
        "headline-stats": {
            all: null,
            albums: Map<number, HeadlineStatsSet>(),
            tracks: Map<number, HeadlineStatsSet>(),
            artists: Map<number, HeadlineStatsSet>(),
        },
    },
    headlineStats: Map<string, HeadlineStatsSet>(),
    requests: {
        "sales": null,
        "demographics": null,
        "playlist-trends": null,
        "headline-stats": null,
    },
    settings: {
        from: getDefaultFromDate(),
        to: getLatestDate(),
        type: "stream",
        countries: [],
        datasetsShown: {},
    },
} as State);
type MapFn = (kind: SalesKind) => (resp: TrendsResponse | [TrendsPaginationResult, any]) => TrendsSet;
type MergeFn = (oldSet: TrendsSet, set: TrendsSet) => TrendsSet;

const makeGetSuccess = <S extends TrendsSet, R extends TrendsResponse | [TrendsPaginationResult, any]>
    (type: TrendsType, mapFn: MapFn, mergeFn?: MergeFn) =>
        (state: State, action: Action<R>): State => {
            const newState = {
                data: {
                    [type]: {},
                },
                requests: {
                    [type]: null,
                },
            } as State;

            // Only update the sales if it matches the last request.
            const request = state.requests[type];
            const response: TrendsResponse | TrendsPaginationResult
                = Array.isArray(action.payload) ? action.payload[0] : action.payload;
            if (request == null ||
                (
                    (
                        (request.from == null && response.from == null) ||
                        equalDateAndMoment(response.from, request.from)
                    ) &&
                    (
                        (request.to == null && response.to == null) ||
                        equalDateAndMoment(response.to, request.to)
                    )
                )
            ) {
                if (mergeFn == null) {
                    mergeFn = (_oldSet, set) => set;
                }
                if (response.albumId != null) {
                    const set = mapFn("album")(action.payload) as S;
                    const oldSet = state.data[type].albums.get(response.albumId);
                    newState.data[type].albums = state.data[type].albums.set(
                        response.albumId,
                        mergeFn(oldSet, set),
                    );
                }
                else if (response.trackId != null) {
                    const set = mapFn("track")(action.payload) as S;
                    const oldSet = state.data[type].tracks.get(response.trackId);
                    newState.data[type].tracks = state.data[type].tracks.set(
                        response.trackId,
                        mergeFn(oldSet, set),
                    );
                }
                else if (response.artistId != null) {
                    const set = mapFn("artist")(action.payload) as S;
                    const oldSet = state.data[type].artists.get(response.artistId);
                    newState.data[type].artists = state.data[type].artists.set(
                        response.artistId,
                        mergeFn(oldSet, set),
                    );
                }
                else {
                    const set = mapFn("all")(action.payload) as S;
                    newState.data[type].all = mergeFn(state.data[type].all, set);
                }
            }
            return update(state, {
                requests: {
                    $merge: newState.requests,
                },
                data: {
                    [type]: {
                        $merge: newState.data[type],
                    },
                },
            });
        };

const mapSalesResponseToSet = (kind: SalesKind, topLevel: boolean = true) => (resp: SalesResponse) => {
    const salesObj = {
        kind,
        from: resp.from,
        to: resp.to,
        groupBy: resp.groupBy,
        data: resp.data,
    } as SalesSet;
    if (topLevel) {
        salesObj.lastLoaded = new Date();
    }
    if (resp.countries != null) {
        salesObj.countries = map(
            resp.countries,
            mapSalesResponseToSet("country", false),
        ) as CountrySalesSet[];
    }
    if (kind === "album") {
        (salesObj as AlbumSalesSet).id = resp.albumId;
        if (resp.tracks != null) {
            (salesObj as AlbumSalesSet).tracks = map(
                resp.tracks,
                mapSalesResponseToSet("track", false),
            ) as TrackSalesSet[];
        }
    }
    else if (kind === "track") {
        (salesObj as TrackSalesSet).id = resp.trackId;
        if (resp.albums != null) {
            (salesObj as TrackSalesSet).albums = map(
                resp.albums,
                mapSalesResponseToSet("album", false),
            ) as AlbumSalesSet[];
        }
    }
    else if (kind === "artist") {
        (salesObj as ArtistSalesSet).id = resp.artistId;
        if (resp.albums != null) {
            (salesObj as ArtistSalesSet).albums = map(
                resp.albums,
                mapSalesResponseToSet("album", false),
            ) as AlbumSalesSet[];
        }
    }
    else if (kind === "country") {
        (salesObj as CountrySalesSet).code = resp.countryCode;
    }

    return salesObj;
};

const mapDemographicsResponseToSet = (kind: SalesKind) => (resp: DemographicsResponse) => {
    const demographicsObj = {
        kind,
        from: resp.from,
        to: resp.to,
        lastLoaded: new Date(),
        data: resp.data,
    } as DemographicsSet;
    if (kind === "album") {
        (demographicsObj as AlbumDemographicsSet).id = resp.albumId;
    }
    else if (kind === "track") {
        (demographicsObj as TrackDemographicsSet).id = resp.trackId;
    }
    else if (kind === "artist") {
        (demographicsObj as ArtistDemographicsSet).id = resp.artistId;
    }

    return demographicsObj;
};

const mapHeadlineStatsResponseToSet = (kind: SalesKind) =>
    ({ from, to, albumId, trackId, artistId, headings, data }: HeadlineStatsResponse) => {
        const headlineStatsObj = {
            kind,
            from,
            to,
            lastLoaded: new Date(),
            headings,
            data,
        } as HeadlineStatsSet;
        if (kind === "album") {
            (headlineStatsObj as AlbumHeadlineStatsSet).id = albumId;
        }
        else if (kind === "track") {
            (headlineStatsObj as TrackHeadlineStatsSet).id = trackId;
        }
        else if (kind === "artist") {
            (headlineStatsObj as ArtistHeadlineStatsSet).id = artistId;
        }

        return headlineStatsObj;
    };

const mapPlaylistTrendsResponseToSet =
    (kind: SalesKind) => ([pagination, result]: [TrendsPaginationResult, PlaylistTrendsResult]) => {
        const playlistTrendsObj = {
            kind,
            from: pagination.from,
            to: pagination.to,
            lastLoaded: new Date(),
            data: result.result,
            pagination,
        } as PlaylistTrendsSet;
        if (kind === "album") {
            (playlistTrendsObj as AlbumPlaylistTrendsSet).id = pagination.albumId;
        }
        else if (kind === "track") {
            (playlistTrendsObj as TrackPlaylistTrendsSet).id = pagination.trackId;
        }
        else if (kind === "artist") {
            (playlistTrendsObj as ArtistPlaylistTrendsSet).id = pagination.artistId;
        }

        return playlistTrendsObj;
    };

const salesGetSuccess = makeGetSuccess<SalesSet, SalesResponse>("sales", mapSalesResponseToSet);
const demographicsGetSuccess = makeGetSuccess<DemographicsSet, DemographicsResponse>(
    "demographics",
    mapDemographicsResponseToSet,
);
const playlistTrendsGetSuccess = makeGetSuccess<PlaylistTrendsSet, [TrendsPaginationResult, PlaylistTrendsResult]>(
    "playlist-trends",
    mapPlaylistTrendsResponseToSet,
    (oldSet: PlaylistTrendsSet, set: PlaylistTrendsSet) => ({
        ...set,
        data: oldSet == null || set.pagination.page === 1
            ? set.data
            : concat(oldSet.data, set.data),
    } as PlaylistTrendsSet),
);
const headlineStatsGetSuccess = makeGetSuccess<HeadlineStatsSet, HeadlineStatsResponse>(
    "headline-stats",
    mapHeadlineStatsResponseToSet,
);

function sales(state: State = getInitialState(), action: actions.SalesAction | ApiAction): State {
    // State should never be null...
    if (state === null) {
        log.warn("sales state setting to null, resetting to initialState.");
        state = {} as State;
        state = update(state, { $set: getInitialState() });
    }

    if (isType(action, actions.requestSalesGet)) {
        // request salesGet
        return requestGet("sales", state, action);
    }
    else if (isType(action, actions.requestDemographicsGet)) {
        // request salesGet
        return requestGet("demographics", state, action);
    }
    else if (isType(action, actions.requestHeadlineStatsGet)) {
        // request salesGet
        return requestGet("headline-stats", state, action);
    }
    else if (isType(action, actions.requestPlaylistTrendsGet)) {
        // request playlistTrendsGet
        return requestGet("playlist-trends", state, action);
    }
    else if (isType(action, actions.salesGetSuccess)) {
        // Successful salesGet
        return salesGetSuccess(state, action);
    }
    else if (isType(action, actions.demographicsGetSuccess)) {
        // Successful demographicsGet
        return demographicsGetSuccess(state, action);
    }
    else if (isType(action, actions.headlineStatsGetSuccess)) {
        // Successful headlineStatsGet
        // First add it to the headline stats section
        const isGlobal = action.payload.albumId == null
            && action.payload.trackId == null
            && action.payload.artistId == null;
        const matchesSettings = state.settings == null
            || (
                (state.settings.from == null || state.settings.from.isSame(action.payload.from, "day"))
                && (state.settings.to == null || state.settings.to.isSame(action.payload.to, "day"))
            );
        let changingState = state;
        if (isGlobal) {
            const key = moment(action.payload.from).format("YYYYMMDD") + "_"
                + moment(action.payload.to).format("YYYYMMDD");
            const set = mapHeadlineStatsResponseToSet("all")(action.payload);
            changingState = update(changingState, {
                headlineStats: {
                    $set: state.headlineStats.set(key, set),
                },
            });
        }
        // Then to the standard section
        if (!isGlobal || matchesSettings) {
            changingState = headlineStatsGetSuccess(changingState, action);
        }
        return changingState;
    }
    else if (isType(action, actions.playlistTrendsGetSuccess)) {
        // Successful playlistTrendsGet
        return playlistTrendsGetSuccess(state, action);
    }
    else if (isType(action, actions.salesGetFailure)) {
        // Failed salesGet
        return getFailure("sales", state, action);
    }
    else if (isType(action, actions.demographicsGetFailure)) {
        // Failed demographicsGet
        return getFailure("demographics", state, action);
    }
    else if (isType(action, actions.headlineStatsGetFailure)) {
        // Failed headlineStatsGet
        return getFailure("headline-stats", state, action);
    }
    else if (isType(action, actions.playlistTrendsGetFailure)) {
        // Failed playlistTrendsGet
        return getFailure("playlist-trends", state, action);
    }
    else if (isType(action, actions.setDateRange)) {
        // set date range
        return setDateRange(state, action);
    }
    else if (isType(action, actions.setCountries)) {
        // set country
        return setCountries(state, action);
    }
    else if (isType(action, actions.toggleStreamsDownloads)) {
        // toggle streams/downloads
        return toggleStreamsDownloads(state, action);
    }
    else if (isType(action, actions.resetChartLegend)) {
        // reset chart legend
        return resetChartLegend(state, action);
    }
    else if (isType(action, actions.updatedChartLegend)) {
        // update chart legend
        return updatedChartLegend(state, action);
    }
    else if (isType(action, logout) || isFailedResponseUnauthenticatedNotLogin(action)) {
        // Clear the state on logout.
        return update(state, { $set: getInitialState() });
    }
    // Otherwise we do nothing
    return state;
}

function requestGet(type: TrendsType, state: State, action: Action<ApiRequest>): State {
    const request = action.payload;
    const requestObj: any = {
        from: request.query("from") != null ? moment(request.query("from")) : null,
        to: request.query("to") != null ? moment(request.query("to")) : null,
        started: new Date(),
    };
    if (request.query("album_id") != null) {
        requestObj.albumId = Number(request.query("album_id"));
    }
    if (request.query("track_id") != null) {
        requestObj.trackId = Number(request.query("track_id"));
    }
    if (request.query("artist_id") != null) {
        requestObj.artistId = Number(request.query("artist_id"));
    }
    return update(state, {
        requests: {
            $merge: {
                [type]: requestObj,
            },
        },
    });
}

function getFailure(type: TrendsType, state: State, _action: Action<StateError>): State {
    // TODO Handle errors
    return update(state, {
        requests: { $merge: { [type]: null } },
    });
}

function setDateRange(state: State, action: Action<[Moment, Moment]>): State {
    return update(state, {
        settings: {
            $merge: {
                from: action.payload[0],
                to: action.payload[1],
            },
        },
    });
}

function setCountries(state: State, action: Action<string[]>): State {
    return update(state, {
        settings: {
            $merge: {
                countries: action.payload,
            },
        },
    });
}

function toggleStreamsDownloads(state: State, _action: Action<{}>): State {
    return update(state, {
        settings: {
            $merge: {
                type: state.settings.type === "stream" ? "download" : "stream",
            },
        },
    });
}

function resetChartLegend(state: State, action: Action<string[]>): State {
    return update(state, {
        settings: {
            datasetsShown: {
                $set: reduce(action.payload,
                    (result: {[key: string]: boolean}, key: string) => {
                        result[key] = true;
                        return result;
                    }, {}),
            },
        },
    });
}

function updatedChartLegend(state: State, action: Action<[string, boolean]>): State {
    const newDatasetsShown: {[key: string]: boolean} = {};
    newDatasetsShown[action.payload[0]] = action.payload[1];
    return update(state, {
        settings: {
            datasetsShown: {
                $merge: newDatasetsShown,
            },
        },
    });
}

export {
    getInitialState,
    sales,
};
